import { useMutation } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { taskQueryKeys } from '@omnea-app/fe-tasks/queryKeys'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { RequestDetailQueryKey } from '../../../useRequestDetail'
import { REQUESTS_DATA_SOURCE_QUERY_KEY } from '../../../utils/useRequestsDataSource'
import { REQUESTS_MOBILE_INFINITE_QUERY_KEY } from '../../../utils/useRequestsMobileInfiniteQuery'
import { pauseDetailQueryKey } from '../paused-banner/useGetPauseDetail'

export interface Pause {
  assigneeId: string
  id: string
  start: string
  note: string | null
  pause_reason: {
    id: string
    text: string
  }
}

interface Props {
  onSuccess?: (data: Pause) => void
  onError?: (error: Error) => void
}

export const usePauseRequest = ({ onSuccess, onError }: Props) => {
  const { t } = useTranslation('requests')
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    mutationFn: async ({
      requestId,
      assigneeId,
      note,
      reasonId,
      reminderDate,
    }: {
      requestId: string
      assigneeId: string
      note: string | null
      reasonId: string
      reminderDate: Date
    }) => {
      const apiClient = new RequestsHttpClient({ authorization: await getToken() })

      const { data, error } = await apiClient.POST('/{id}/pause', {
        body: { assigneeId, note, reasonId, reminderDate: reminderDate.toISOString() },
        params: { path: { id: requestId } },
      })

      throwIfApiError(error)

      return data
    },
    onSuccess: (data, { requestId }, _, { client }) => {
      void client.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] })
      void client.invalidateQueries({ queryKey: pauseDetailQueryKey(requestId) })
      void client.invalidateQueries({ queryKey: ['dashboard-requests'] })
      void client.invalidateQueries({ queryKey: ['dashboard-requests-stats'] })
      void client.invalidateQueries({ queryKey: taskQueryKeys.all })
      void client.invalidateQueries({ queryKey: [REQUESTS_DATA_SOURCE_QUERY_KEY] })
      void client.invalidateQueries({ queryKey: [REQUESTS_MOBILE_INFINITE_QUERY_KEY] })
      onSuccess?.(data)
    },
    onError: (error) => {
      enqueueSnackbar(t('pause.errors.pause-toast'), { variant: 'error' })
      onError?.(error)
    },
  })
}
